/**
 * Knowledge Base Types
 *
 * These types define the structure for documentation articles
 * that power both the Help page UI and the Virtual CISO chatbot context.
 */

export interface KnowledgeArticle {
  /** Unique identifier for the article */
  id: string

  /** Display title */
  title: string

  /** Parent section ID */
  section: string

  /** Full markdown content for display */
  content: string

  /** 1-2 sentence summary for AI context injection */
  summary: string

  /** Tags for search and AI relevance matching */
  tags: string[]

  /** Related page paths (e.g., ['/controls', '/risks']) */
  relatedPages: string[]

  /** Lucide icon name (optional) */
  icon?: string

  /** Sort order within section */
  order: number

  /** ISO date string for freshness tracking */
  lastUpdated: string
}

export interface KnowledgeSection {
  /** Unique identifier for the section */
  id: string

  /** Display title */
  title: string

  /** Lucide icon name */
  icon: string

  /** Brief description of the section */
  description: string

  /** Sort order */
  order: number

  /** Articles in this section */
  articles: KnowledgeArticle[]
}

export interface KnowledgeBase {
  /** All sections with their articles */
  sections: KnowledgeSection[]

  /** Flat map of all articles by ID for quick lookup */
  articleMap: Record<string, KnowledgeArticle>

  /** Precomputed data for AI context */
  aiContext: {
    /** All article summaries concatenated */
    summaryText: string

    /** Map of page paths to relevant article IDs */
    pageMap: Record<string, string[]>

    /** Map of tags to article IDs */
    tagMap: Record<string, string[]>
  }
}

/**
 * Helper to create an article with defaults
 */
export function createArticle(
  partial: Omit<KnowledgeArticle, 'lastUpdated'> & { lastUpdated?: string }
): KnowledgeArticle {
  return {
    ...partial,
    lastUpdated: partial.lastUpdated || new Date().toISOString().split('T')[0],
  }
}

/**
 * Build the AI context from articles
 */
export function buildAIContext(articles: KnowledgeArticle[]): KnowledgeBase['aiContext'] {
  const summaryText = articles
    .map(a => `[${a.title}]: ${a.summary}`)
    .join('\n')

  const pageMap: Record<string, string[]> = {}
  const tagMap: Record<string, string[]> = {}

  for (const article of articles) {
    // Build page map
    for (const page of article.relatedPages) {
      if (!pageMap[page]) pageMap[page] = []
      pageMap[page].push(article.id)
    }

    // Build tag map
    for (const tag of article.tags) {
      if (!tagMap[tag]) tagMap[tag] = []
      tagMap[tag].push(article.id)
    }
  }

  return { summaryText, pageMap, tagMap }
}
